import { useEffect } from "react";

const SITE = {
  name: "MASTOFA.COM",
  url: "https://mastofa.com",
  logo: "https://customer-assets.emergentagent.com/job_web-hub-25/artifacts/w3d5x69k_MastofaAcademy.jpg",
  wa: "+6285157009099",
  address: {
    street: "Jl. Soekarno Hatta No. 381, Bugangin",
    city: "Kendal",
    region: "Jawa Tengah",
    postal: "51314",
    country: "ID",
  },
};

/** Upsert a meta / link tag in <head>. */
function upsert(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement(sel.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

/** Insert/replace a JSON-LD script by id. */
function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * useSeo — set title, meta description, canonical, and Open Graph tags.
 *
 * @param {object} opts
 * @param {string} opts.title       — page title (short, becomes <title>)
 * @param {string} opts.description — meta description (max 160 chars)
 * @param {string} opts.path        — canonical path (e.g. "/jasa-iklan")
 * @param {string} opts.image       — OG image URL (defaults to logo)
 * @param {string} opts.type        — og:type (default: website)
 * @param {object} opts.jsonLd      — optional JSON-LD object
 * @param {string} opts.jsonLdId    — id for the JSON-LD script tag
 */
export function useSeo({
  title,
  description = "MASTOFA.COM — Digital Marketing, Education, Consulting. Jasa Iklan Meta / TikTok / Shopee, Landing Page, Privat 1-on-1, VIP Member, dan E-course praktis.",
  path = "",
  image,
  type = "website",
  jsonLd,
  jsonLdId = "seo-jsonld-page",
} = {}) {
  useEffect(() => {
    document.head.setAttribute("data-seo-set", "1");
    const alreadyBranded = title && title.toLowerCase().includes(SITE.name.toLowerCase());
    const fullTitle = title
      ? (alreadyBranded ? title : `${title} — ${SITE.name}`)
      : `${SITE.name} — Digital Marketing, Education, Consulting`;
    document.title = fullTitle;

    upsert('meta[name="description"]', { name: "description", content: description });
    upsert('meta[name="robots"]', { name: "robots", content: "index, follow" });

    const url = `${SITE.url}${path || (typeof window !== "undefined" ? window.location.pathname : "")}`;
    upsert('link[rel="canonical"]', { rel: "canonical", href: url });

    // Open Graph
    upsert('meta[property="og:site_name"]', { property: "og:site_name", content: SITE.name });
    upsert('meta[property="og:type"]', { property: "og:type", content: type });
    upsert('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsert('meta[property="og:description"]', { property: "og:description", content: description });
    upsert('meta[property="og:url"]', { property: "og:url", content: url });
    upsert('meta[property="og:image"]', { property: "og:image", content: image || SITE.logo });
    upsert('meta[property="og:locale"]', { property: "og:locale", content: "id_ID" });

    // Twitter
    upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsert('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsert('meta[name="twitter:image"]', { name: "twitter:image", content: image || SITE.logo });

    if (jsonLd) {
      upsertJsonLd(jsonLdId, jsonLd);
    }

    return () => {
      // Clean up per-page JSON-LD when unmounting.
      if (jsonLd) {
        const el = document.getElementById(jsonLdId);
        if (el) el.remove();
      }
    };
  }, [title, description, path, image, type, jsonLd, jsonLdId]);
}

/** Organization / LocalBusiness — mount once from App. */
export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: SITE.name,
  url: SITE.url,
  logo: SITE.logo,
  image: SITE.logo,
  description:
    "MASTOFA.COM — Digital Marketing, Education, Consulting. Jasa Iklan Meta / TikTok / Shopee, Landing Page, Privat 1-on-1, VIP Member, dan E-course.",
  telephone: SITE.wa,
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.region,
    postalCode: SITE.address.postal,
    addressCountry: SITE.address.country,
  },
  areaServed: "ID",
  sameAs: [],
};

export const jsonLdService = ({ name, description, price, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  provider: { "@type": "Organization", name: SITE.name, url: SITE.url },
  description,
  areaServed: "ID",
  url: `${SITE.url}${path}`,
  ...(price
    ? {
        offers: {
          "@type": "Offer",
          price: String(price),
          priceCurrency: "IDR",
          availability: "https://schema.org/InStock",
        },
      }
    : {}),
});

export const jsonLdCourse = ({ name, description, path, price }) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name,
  description,
  provider: { "@type": "Organization", name: SITE.name, url: SITE.url },
  url: `${SITE.url}${path}`,
  ...(price
    ? {
        offers: {
          "@type": "Offer",
          price: String(price),
          priceCurrency: "IDR",
          category: "Digital Course",
        },
      }
    : {}),
});

export const jsonLdFaq = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (items || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

/** Mount Organization schema once — call from App.js. */
export function OrganizationSchema() {
  useEffect(() => {
    upsertJsonLd("seo-jsonld-org", ORGANIZATION_JSONLD);
  }, []);
  return null;
}
